import React, { useRef, useState } from "react";
import { format } from "date-fns";
import { Calendar, CheckCircle2, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { saveOrganisationConnections } from "@/utils/organization/saveOrganization";
import { isInventorySnapshotConnectionId } from "@/shared/inventoryCsvConnectionChannels";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Json } from "@/integrations/supabase/types";
import { useQueryClient } from "@tanstack/react-query";
import {
  mergeThousandsIfLastColumn,
  normalizeInventoryCsvRow,
  parseInventoryQuantity,
  trimTrailingEmpty,
} from "@/utils/csvQuantity";
import { resolveIdentifierColumn } from "@/shared/identifierResolution/resolveIdentifierColumn";
import { formatUnsupportedIdentifierMessage } from "@/shared/identifierResolution/unsupportedIdentifierMessage";
import { normalizeIdentifierHeader } from "@/shared/identifierResolution/standardIdentifierTypes";
import {
  customCanonicalMap,
  customNamesFromRows,
  useOrganisationCustomIdentifiers,
} from "@/hooks/useOrganisationCustomIdentifiers";

interface InventoryItem {
  identifier: string;
  quantity: number;
  // UC-2 rework cycle 5 (2026-07-13, BR-UC2-6) — optional raw description
  // text, captured only when the uploaded file includes a recognised
  // description column (see DESCRIPTION_HEADERS below). Undefined for any
  // existing CSV with no such column — fully backward compatible.
  description?: string;
}

type Step = "configure" | "success";

interface Props {
  organisationId: string;
  open: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
}

export interface ParseCsvOptions {
  customNames?: string[];
  customCanonicalByName?: Record<string, string>;
  /** Display preference only — must not gate identifier acceptance (AC-169-08/09). */
  primaryProductIdentifier?: string;
}

// UC-2 rework cycle 5 (2026-07-13, BR-UC2-6) — optional description column
// recognised by header name (case-insensitive, matching IDENTIFIER_HEADERS'
// convention). Purely additive: a CSV with none of these headers behaves
// identically to before this rework cycle (descriptionIndex stays -1,
// InventoryItem.description stays undefined for every row).
const DESCRIPTION_HEADERS = ["description", "product description", "item description", "desc"];

export function parseCsv(
  content: string,
  options: ParseCsvOptions = {},
): { items: InventoryItem[]; identifierType: string; error?: string } {
  void options.primaryProductIdentifier;
  const lines = content.split("\n").filter((l) => l.trim());
  if (lines.length === 0) return { items: [], identifierType: "sku", error: "CSV file is empty" };

  const rawHeaders = trimTrailingEmpty(
    lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, "")),
  );
  const header = rawHeaders.map((h) => h.toLowerCase());
  const resolveOpts = {
    customNames: options.customNames ?? [],
    customCanonicalByName: options.customCanonicalByName,
  };
  const resolutions = rawHeaders.map((h) => resolveIdentifierColumn(h, resolveOpts));
  const permittedIndexes = resolutions
    .map((r, i) => (r.kind === "unsupported" ? -1 : i))
    .filter((i) => i >= 0);

  const seenCanonical = new Map<string, number>();
  for (const i of permittedIndexes) {
    const key = normalizeIdentifierHeader(resolutions[i].canonicalType);
    if (seenCanonical.has(key)) {
      const first = rawHeaders[seenCanonical.get(key)!];
      return {
        items: [],
        identifierType: "sku",
        error: `CSV has conflicting identifier columns after normalization: "${first}" and "${rawHeaders[i]}" both resolve to ${resolutions[i].canonicalType}.`,
      };
    }
    seenCanonical.set(key, i);
  }

  const identifierIndex = permittedIndexes[0] ?? -1;
  const quantityIndex = header.findIndex((h) => h === "quantity" || h === "qty");
  const descriptionIndex = header.findIndex((h) => DESCRIPTION_HEADERS.includes(h));

  if (identifierIndex === -1) {
    return {
      items: [],
      identifierType: "sku",
      error: formatUnsupportedIdentifierMessage(options.customNames ?? []),
    };
  }
  if (quantityIndex === -1) return { items: [], identifierType: "sku", error: 'CSV must contain a "Quantity" or "Qty" column' };

  const identifierType = resolutions[identifierIndex].canonicalType;
  const items: InventoryItem[] = [];
  const seen = new Set<string>();

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values: string[] = [];
    let current = "";
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') { inQuotes = !inQuotes; }
      else if (char === "," && !inQuotes) { values.push(current.trim().replace(/^"|"$/g, "")); current = ""; }
      else { current += char; }
    }
    values.push(current.trim().replace(/^"|"$/g, ""));

    const row = normalizeInventoryCsvRow(values, header.length, quantityIndex);
    const identifier = row[identifierIndex]?.trim();
    if (!identifier) continue;

    const quantityRaw = mergeThousandsIfLastColumn(row, quantityIndex, header.length);
    const quantity = parseInventoryQuantity(quantityRaw);
    if (quantity === null) continue;

    // Post-delivery rework (2026-08-18, Bug C): dedup must be case-SENSITIVE
    // (trim only) per AC-UC162-16 / H3-BLOCK-1 Option A — case is part of
    // identifier identity, so distinct case variants (e.g. "Apple"/"apple")
    // must not collapse into a single row before the request even reaches
    // import-inventory-csv-v3.
    if (seen.has(identifier)) continue;
    seen.add(identifier);

    const description = descriptionIndex !== -1 ? row[descriptionIndex]?.trim() : undefined;
    items.push(description ? { identifier, quantity, description } : { identifier, quantity });
  }

  if (items.length === 0) return { items: [], identifierType, error: "No valid inventory rows found in CSV" };
  if (items.length > 10_000) return { items: [], identifierType, error: "Maximum 10,000 items per upload" };

  return { items, identifierType };
}

const SnapshotCsvUpload: React.FC<Props> = ({ organisationId, open, onClose, onUploadSuccess }) => {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const customIdentifiersQuery = useOrganisationCustomIdentifiers(open ? organisationId : undefined);
  const customNames = customNamesFromRows(customIdentifiersQuery.data);
  const customCanonicalByName = customCanonicalMap(customIdentifiersQuery.data);

  const [step, setStep] = useState<Step>("configure");
  const [file, setFile] = useState<File | null>(null);
  const [date, setDate] = useState<Date>(new Date());
  const [calOpen, setCalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [successDate, setSuccessDate] = useState("");
  const [ecommerceSubtype, setEcommerceSubtype] = useState<"amazon" | "shopify">("amazon");

  const effectiveDate = format(date, "yyyy-MM-dd");

  const isInventorySnapshotConnection = (connection: any): boolean => {
    if (!connection || typeof connection !== "object") return false;
    const type = String(connection.type || "").toLowerCase();
    const service = String(connection.service || "").toLowerCase();
    const importType = String(connection.import_type || "").toLowerCase();
    const connectionId = String(connection.id || connection.connectionId || "").toLowerCase();
    return (
      (type === "csv" || service === "csv") &&
      importType === "ecommerce" &&
      isInventorySnapshotConnectionId(connectionId)
    );
  };

  const matchesSameSnapshotSlice = (connection: any, snapshotDate: string, subtype: "amazon" | "shopify"): boolean => {
    if (!isInventorySnapshotConnection(connection)) return false;
    const connSubtype = String(connection.ecommerce_subtype || "").toLowerCase();
    if (connSubtype !== subtype) return false;
    const metadataDate = connection?.metadata?.inventory_date;
    if (typeof metadataDate !== "string" || metadataDate.length === 0) return false;
    return metadataDate.slice(0, 10) === snapshotDate;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
  };

  const handleUpload = async () => {
    if (!file) return;

    let content: string;
    if (/\.xlsx?$/i.test(file.name)) {
      try {
        const ExcelJS = await import("exceljs");
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(await file.arrayBuffer());
        const sheet = wb.worksheets[0];
        const rows: string[] = [];
        sheet.eachRow((row) => {
          const cells = (row.values as unknown[]).slice(1);
          rows.push(cells.map((c) => {
            const v = c == null ? "" : String(c);
            return v.includes(",") || v.includes('"') || v.includes("\n")
              ? `"${v.replace(/"/g, '""')}"` : v;
          }).join(","));
        });
        content = rows.join("\n");
      } catch {
        toast.error("Failed to parse Excel file");
        return;
      }
    } else {
      content = await file.text();
    }

    const { items, identifierType, error } = parseCsv(content, {
      customNames,
      customCanonicalByName,
    });
    if (error || items.length === 0) {
      toast.error(error ?? "No valid rows found");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error: fnErr } = await invokeFunction(supabase, "import-inventory-csv-v3", {
        body: {
          organisationId,
          snapshotDate: effectiveDate,
          inventory: items.map((i) => ({ identifier: i.identifier, quantity: i.quantity, description: i.description })),
          identifierType,
          ecommerceSubtype,
          // Post-delivery rework (Bug A2): thread the uploaded file's name
          // through so identifier_observation.source_detail is populated
          // for this ingestion surface, matching background-csv-import-worker-v3.
          fileName: file.name,
        },
      });

      if (fnErr || data?.error) {
        throw new Error(data?.error ?? fnErr?.message ?? "Import failed");
      }

      // Save connection metadata so the Data tab reflects this upload — same logic as
      // CsvImportStep.handleCsvSuccess in the org edit flow.
      const snapshotConnectionId = data?.connectionId;
      const baseConnectionId = `csv-import-${Date.now()}`;
      const normalizedDate = effectiveDate.replace(/-/g, "");
      const connectorId = `${baseConnectionId}_inventory_${ecommerceSubtype}_${normalizedDate}`;
      const connector = {
        id: connectorId,
        name: "Inventory CSV Import",
        description: `Inventory file: ${file.name}`,
        logoUrl: "https://via.placeholder.com/40?text=INV",
        status: "connected",
        connectorId: baseConnectionId,
        connectionId: baseConnectionId,
        service: "csv",
        type: "csv",
        file_name: file.name,
        import_type: "ecommerce",
        ecommerce_subtype: ecommerceSubtype,
        created_at: new Date().toISOString(),
        metadata: {
          snapshot_connection_id: snapshotConnectionId,
          inventory_date: effectiveDate,
          inventory_source: ecommerceSubtype,
        },
      };

      try {
        const { data: currentOrg, error: fetchErr } = await supabase
          .from("organisations")
          .select("connections")
          .eq("id", organisationId)
          .single();

        if (fetchErr) {
          throw fetchErr;
        }

        const currentConnections = currentOrg?.connections;
        let nextConnections: Json;

        if (Array.isArray(currentConnections)) {
          const filtered = currentConnections.filter(
            (conn: any) => !matchesSameSnapshotSlice(conn, effectiveDate, ecommerceSubtype)
          );
          nextConnections = [...filtered, connector] as unknown as Json;
        } else if (currentConnections && typeof currentConnections === "object") {
          const entries = Object.entries(currentConnections as Record<string, unknown>).filter(
            ([, conn]) => !matchesSameSnapshotSlice(conn, effectiveDate, ecommerceSubtype)
          );
          nextConnections = Object.fromEntries([
            ...entries,
            [connector.id, connector],
          ]) as unknown as Json;
        } else {
          nextConnections = [connector] as unknown as Json;
        }

        const { error: updateErr } = await saveOrganisationConnections(organisationId, nextConnections);

        if (updateErr) {
          throw updateErr;
        }
      } catch {
        // Non-fatal — import succeeded; Data tab will be stale but snapshot data is saved.
        console.error("SnapshotCsvUpload: failed to update organisations.connections");
      }

      setSuccessCount(data?.itemCount ?? items.length);
      setSuccessDate(effectiveDate);
      setStep("success");
      queryClient.invalidateQueries({ queryKey: ["adjustment-deltas", organisationId] });
      queryClient.invalidateQueries({ queryKey: ["inventory-audit-log", organisationId] });
      onUploadSuccess?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Import failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep("configure");
    setFile(null);
    setSuccessCount(0);
    setSuccessDate("");
    setEcommerceSubtype("amazon");
    if (fileRef.current) fileRef.current.value = "";
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose(); }}>
      <DialogContent className="max-w-lg">
        {step === "configure" && (
          <>
            <DialogHeader>
              <DialogTitle>Upload inventory snapshot</DialogTitle>
              <DialogDescription>
                Upload a CSV or Excel file. The file must have an identifier column (SKU, UPC,
                ASIN…) and a <strong>Quantity</strong> column. Data is written to the snapshot
                database for reporting. This upload becomes the source-of-truth baseline and resets
                reconciliation to sales/adjustments after this snapshot date.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Platform source</Label>
                <Select value={ecommerceSubtype} onValueChange={(v) => setEcommerceSubtype(v as "amazon" | "shopify")}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amazon">Amazon</SelectItem>
                    <SelectItem value="shopify">Shopify</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Snapshot date</Label>
                <Popover open={calOpen} onOpenChange={setCalOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-full justify-start text-left font-normal")}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {format(date, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={date}
                      onSelect={(d) => { if (d) { setDate(d); setCalOpen(false); } }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>CSV or Excel file</Label>
                <div
                  className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  {file ? (
                    <p className="text-sm font-medium">{file.name}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Click to select a CSV or Excel file
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Must include identifier + Quantity columns
                  </p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleUpload} disabled={!file || isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "success" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Snapshot imported
              </DialogTitle>
              <DialogDescription>
                Inventory snapshot saved to the reporting database.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md border bg-muted/30 p-4 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Items imported</span>
                <span className="font-medium">{successCount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Snapshot date</span>
                <span className="font-medium">{successDate}</span>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SnapshotCsvUpload;
